import { Button } from "@chakra-ui/react";
import { RpcError } from "grpc-web";
import { FunctionComponent, ReactElement, useEffect, useState } from "react";

// Components
import CustomModal from "../../shared/Modals/CustomModal";

// Hooks
import { useAuth } from "../../hooks/useAuth";
import usePermission from "../../hooks/usePermission";
import usePolling from "../../hooks/usePolling";
import useClaimsToast from "../../hooks/useToast";

// Utils
import { exportSelectedClaimsToCsv, getExportCsvStatus } from "../../utils/api/claims";
import { UserRole } from "../../utils/consts";
interface ExportSelectedClaimsButtonProps {
	selectedClaimIds: Array<number>;
	onExport(): void;
}

/**
 * Button component to export selected approved claims for payment
 * Follows the same pattern as NewPaymentExportButton
 * @param props typeof: ExportSelectedClaimsButtonProps
 * @returns ReactElement
 */
const ExportSelectedClaimsButton: FunctionComponent<ExportSelectedClaimsButtonProps> = (
	props: ExportSelectedClaimsButtonProps
): ReactElement => {
	const { selectedClaimIds, onExport } = props;
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
	const { getUser } = useAuth();
	const { hasPaymentsExportAccess } = usePermission();
	const user = getUser();
	const toast = useClaimsToast();
	const { data: exportStatus } = usePolling(getExportCsvStatus);
	const [isExportRequested, setIsExportRequested] = useState<boolean>(false);
	const isExportInProgress = exportStatus?.getInProgress() ?? false;
	const isExportDisabled =
		!hasPaymentsExportAccess || selectedClaimIds.length === 0 || isExportInProgress || isExportRequested;

	useEffect(() => {
		if (exportStatus && !exportStatus.getInProgress()) {
			setIsExportRequested(false);
		}
	}, [exportStatus]);

	const handleExportClaims = async (): Promise<void> => {
		setIsExportRequested(true);
		setIsLoading(true);

		await exportSelectedClaimsToCsv(selectedClaimIds)
			.then(() => {
				toast("Success", `Your export is processing, please allow a few moments for processing`, "success");
			})
			.catch((err) => {
				console.error((err as RpcError).message);
				toast("Error", (err as RpcError).message, "error");
			})
			.finally(() => {
				setIsLoading(false);
				if (isExportModalOpen) setIsExportModalOpen(false);
				onExport();
			});
	};

	const getExportModal = (): ReactElement => {
		const claimCount = selectedClaimIds.length;
		return (
			<CustomModal
				isOpen={isExportModalOpen}
				title={"Export Selected Claims"}
				size="lg"
				message={`${claimCount} ${claimCount > 1 ? "Claims are" : "Claim is"} ready to be Queued for Payment. Continue?`}
				onContinue={() => handleExportClaims()}
				onCancel={() => setIsExportModalOpen(false)}
				continueText={"Okay"}
				continueLoading={isLoading}
			/>
		);
	};

	const canUserExport = (): void => {
		const allowedRoles: Array<UserRole> = [UserRole.ClaimsAdmin, UserRole.Payments];
		if (user?.role.split(",").some((role: string) => allowedRoles.includes(role as UserRole))) {
			setIsExportModalOpen(true);
		} else {
			toast("Access Denied", "You do not have the correct permission to create a payments export", "warning");
		}
	};

	return (
		<>
			{isExportModalOpen && getExportModal()}
			<Button size="sm" onClick={() => canUserExport()} isDisabled={isExportDisabled} colorScheme="orange" padding={2}>
				Export Selected Claims ({selectedClaimIds.length})
			</Button>
		</>
	);
};

export default ExportSelectedClaimsButton;
